<script setup>
import i18next from 'i18next';
import { computed, onMounted, ref, watch } from 'vue';
import { fetchData } from '../../fetchData';
import CategoryFilterSkeleton from './CategoryFilterSkeleton.vue';

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['update:modelValue']);
const categories = ref([]);
const selectedCategoryIds = ref(props.modelValue);
const loading = ref(true);
const error = ref(null);
const language = i18next.language || 'de';

const isAllSelected = computed(() => selectedCategoryIds.value.length === 0);

watch(
    () => props.modelValue,
    (newValue) => {
        selectedCategoryIds.value = newValue;
    }
);

const fetchCategories = async () => {
    error.value = null;
    try {
        const { result = {}, pending } = await fetchData('/api/v1/posts/postCategories');
        loading.value = pending;
        categories.value = result.postCategories;
    } catch (e) {
        error.value = e.message;
    } finally {
        loading.value = false;
    }
};

const isSelected = (category) => selectedCategoryIds.value.includes(category.id);

const toggleCategory = (category) => {
    const index = selectedCategoryIds.value.indexOf(category.id);

    if (index > -1) {
        selectedCategoryIds.value.splice(index, 1);
    } else {
        selectedCategoryIds.value.push(category.id);
    }

    emitSelected();
};

const clearSelection = () => {
    selectedCategoryIds.value = [];
    emitSelected();
};

const emitSelected = () => {
    emit('update:modelValue', selectedCategoryIds.value);
};

onMounted(fetchCategories);
</script>

<template>
    <div class="flex gap-2 flex-wrap">
        <div v-if="loading" class="flex gap-2 flex-wrap">
            <CategoryFilterSkeleton v-for="n in 5" :key="n" />
        </div>
        <div v-else-if="error" class="w-full text-center py-2 text-red-500">
            <span>{{ $t('failed_loading_categories') }}</span>
        </div>
        <div v-else class="overflow-x-auto flex flex-row flex-nowrap gap-2">
            <div :class="[
                'flex shrink-0 pointer items-center whitespace-nowrap text-xs p-2 rounded-full cursor-pointer',
                isAllSelected ? 'bg-blue text-white border border-blue' : 'border border-blue text-blue hover:bg-blue hover:text-white'
            ]" @click="clearSelection">
                <span> {{ $t('plain_all') }} </span>
            </div>
            <div v-for="category in categories" :key="category.id">
                <div :class="[
                    'flex shrink-0 pointer items-center whitespace-nowrap text-xs p-2 rounded-full cursor-pointer',
                    isSelected(category) ? 'bg-blue text-white border border-blue' : 'border border-blue text-blue hover:bg-blue hover:text-white'
                ]" @click="toggleCategory(category)">
                    <div v-if="isSelected(category)"
                        class="hidden lg:inline-flex mr-1 w-4 h-4 items-center justify-center">
                        <i class="fi fi-rs-cross-small mt-1 text-white"></i>
                    </div>
                    <span>{{ category.name[language] }}</span>
                </div>
            </div>
        </div>
    </div>
</template>
